import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  NavLink, useNavigate, useLocation, Outlet, Navigate
} from 'react-router-dom';
import { string } from 'prop-types';
import { Translate } from 'react-localize-redux';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import ScrollToTop from '../ScrollToTop';
import { routes } from '../../constants';

import styles from './startSurvey.module.css';
import { langType, surveyTypeValues } from '../../constants/types';
import InfoBox from '../InfoBox';
import InfoRow from '../InfoBox/InfoRow';
import { getTranslation, withLang } from '../../i18n';
import { getSubRouteForSurveyType, setDocumentTitle } from '../../util';

const StepIndicator = ({ path, translateKey }) => (
  <NavLink
    to={path}
    className={({ isActive }) => (isActive ? `${styles.activeStepIndicator} ${styles.stepIndicator}` : styles.stepIndicator)}
  ><Translate id={translateKey} />
  </NavLink>
);

StepIndicator.propTypes = {
  path: string.isRequired,
  translateKey: string.isRequired
};

const renderDegreeProgramSurveyStepIndicators = (subRoute) => (
  <nav className={styles.stepIndicatorContainer}>
    <StepIndicator path={subRoute.startSurvey.contents.index} translateKey="startSurvey.content" />
    <StepIndicator path={subRoute.startSurvey.messages} translateKey="startSurvey.messages" />
    <StepIndicator path={subRoute.startSurvey.participants} translateKey="startSurvey.participantsAndScheduling" />
    <StepIndicator path={subRoute.startSurvey.researchConsent} translateKey="startSurvey.researchConsent.title" />
  </nav>
);

const renderCourseSurveyStepIndicators = () => (
  <nav className={styles.stepIndicatorContainer}>
    <StepIndicator path={routes.surveys.husSurveys.startSurvey.contents.index} translateKey="startSurvey.content" />
    <StepIndicator path={routes.surveys.husSurveys.startSurvey.messages} translateKey="startSurvey.scheduling" />
    <StepIndicator path={routes.surveys.husSurveys.startSurvey.participants} translateKey="startSurvey.participantsAndScheduling" />
  </nav>
);

const renderStepIndicators = (surveyType, surveyTypeSubRoute) => {
  switch (surveyType) {
    case surveyTypeValues.COURSE_SURVEY:
      return renderCourseSurveyStepIndicators();
    default:
      return renderDegreeProgramSurveyStepIndicators(surveyTypeSubRoute);
  }
};

const StartSurvey = ({
  translate: t,
  lang
}) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { surveyType, schemaTitle, schemaVersion } = useSelector((state) => state.survey.surveyOptions);
  const surveyTypeSubRoute = getSubRouteForSurveyType(surveyType);

  // Redirect to the front page if accessing the start survey form illegitimately
  // (e.g., via browser back button after submission).
  if (!surveyType) {
    return <Navigate to="/" />;
  }

  useEffect(() => {
    if (pathname === surveyTypeSubRoute.startSurvey.index) {
      navigate(surveyTypeSubRoute.startSurvey.contents.index, { replace: true });
    }
  }, []);

  useEffect(() => {
    setDocumentTitle(t('startSurvey.startSurvey'));
  }, [pathname]);

  return (
    <TransitionGroup>
      <CSSTransition key={pathname} classNames="fade" timeout={300} exit={false}>
        <div className="page-container">
          <ScrollToTop />
          <h1 className="page-title--small">{t('startSurvey.startSurvey')}</h1>
          <InfoBox title={t('startSurvey.schemaInfo.title')}>
            <>
              <InfoRow title={t('common.type')} content={t(`surveyType.${surveyType}`)} dataCy="type" />
              <InfoRow title={t('common.schema')} content={getTranslation(schemaTitle, lang)} dataCy="schema" />
              <InfoRow title={t('startSurvey.schemaInfo.version')} content={schemaVersion} dataCy="version" />
            </>
          </InfoBox>
          { renderStepIndicators(surveyType, surveyTypeSubRoute) }
          <Outlet />
        </div>
      </CSSTransition>
    </TransitionGroup>
  );
};

StartSurvey.propTypes = {
  ...langType
};

export default withLang(StartSurvey);
